import React from 'react';
import { Sparkles, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';

/**
 * OCRConfidenceBadge — shows an AI confidence indicator for an extracted field.
 *
 * Props:
 *  confidence  – number 0..1 (e.g. 0.97) or 0..100
 *  label       – optional tooltip / label override
 *  compact     – boolean, shows only an icon dot instead of full badge
 */
export default function OCRConfidenceBadge({ confidence, label, compact = false }) {
  if (confidence === undefined || confidence === null) return null;

  // Normalise to 0-100
  const pct = confidence > 1 ? confidence : confidence * 100;

  let tier, color, Icon, bg, ring;
  if (pct >= 90) {
    tier = 'High'; color = 'text-emerald-700'; bg = 'bg-emerald-50'; ring = 'border-emerald-200';
    Icon = CheckCircle2;
  } else if (pct >= 65) {
    tier = 'Medium'; color = 'text-amber-700'; bg = 'bg-amber-50'; ring = 'border-amber-300';
    Icon = AlertTriangle;
  } else {
    tier = 'Low'; color = 'text-red-700'; bg = 'bg-red-50'; ring = 'border-red-300';
    Icon = HelpCircle;
  }

  const tooltipText = label || `AI confidence: ${pct.toFixed(0)}% (${tier})`;

  if (compact) {
    return (
      <span
        title={tooltipText}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${bg} border ${ring} cursor-help`}
      >
        <Icon className={`w-2.5 h-2.5 ${color}`} />
      </span>
    );
  }

  return (
    <span
      title={tooltipText}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${bg} ${ring} ${color} cursor-help`}
    >
      <Sparkles className="w-2.5 h-2.5 opacity-70" />
      {pct.toFixed(0)}%
    </span>
  );
}

/**
 * OCRFieldHighlight — wrapper for a form field that highlights low-confidence AI-extracted values.
 *
 * Props:
 *  confidence  – 0..1 (from invoice OCR data)
 *  label       – field label string
 *  children    – the field input/display content
 */
export function OCRFieldHighlight({ confidence, label, children }) {
  if (confidence === undefined || confidence === null) {
    return (
      <div>
        <label className="text-sm font-medium text-gray-500">{label}</label>
        {children}
      </div>
    );
  }

  const pct = confidence > 1 ? confidence : confidence * 100;
  const isLow = pct < 65;
  const isMedium = pct >= 65 && pct < 90;

  return (
    <div className={`rounded-lg p-0.5 ${isLow ? 'bg-red-50 border border-red-200' : isMedium ? 'bg-amber-50 border border-amber-200' : ''}`}>
      <div className={isLow || isMedium ? 'p-2' : ''}>
        <div className="flex items-center gap-1.5 mb-1">
          <label className="text-sm font-medium text-gray-500">{label}</label>
          <OCRConfidenceBadge confidence={confidence} />
          {isLow && (
            <span className="text-xs text-red-600 font-medium">— Please verify</span>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
